import { UpgradeOffer } from '@pubstack/common/src/upgrade'
import { FunctionComponent, ReactNode, useEffect, useRef } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { Colors } from '~/assets/style/colors'
import Button from '~/components/Button'
import { useGlobalFlyout } from '~/components/layout/GlobalFlyout'
import { useGlobalModal } from '~/components/layout/GlobalModal'
import { PageContent } from '~/components/PageContent'
import { TabProp, Tabs } from '~/components/Tabs'
import { useBreadcrumbs } from '~/hooks/useBreadcrumbs'
import { useNodeStaffAccess } from '~/hooks/useNodeStaffAccess'
import { useScope } from '~/hooks/useScope'
import { scopeOwnsOffer } from '~/modules/monetization/upgrade/scopeOffers'
import { PureAdmAdUnitHelpFlyout } from '~/modules/adm/inventory/adunits/PureAdmAdUnitHelpFlyout'
import { AdUnitsHelpFlyout } from '~/modules/pbs/adunits/AdUnitsHelpFlyout'
import { AD_UNIT_TABS, isTabAvailable } from './adUnitTab'
import { LockedTableDrawing } from '~/modules/monetization/upgrade/UpgradeLockedPanel'
import { UpgradeModal } from '~/modules/monetization/upgrade/UpgradeModal'

const HELP_FLYOUT_WIDTH = '600px'

/** Both products, since a scope on neither is not choosing between them yet. */
const UPGRADE_OFFERS_TO_SELL: UpgradeOffer[] = ['one', 'node']

/**
 * One Ad units page for the whole inventory: One's web ad units and Node's server-side, in-app and AMP
 * ones, which used to be two pages under two products.
 *
 * All four tabs render for every scope, including the ones it does not own — a tab whose product is
 * missing shows the upgrade pitch where its table would be. Hiding them is what made the products
 * invisible to the customers who might buy them.
 */
export const AdUnitsTabsPage: FunctionComponent = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const breadcrumbs = useBreadcrumbs()
  const scope = useScope()
  const { hasNode, isStaffOnNode } = useNodeStaffAccess()
  const products = { hasOne: scopeOwnsOffer(scope, 'one'), hasNode }
  const flyout = useGlobalFlyout()

  const segments = location.pathname.split('/')
  const activePath = AD_UNIT_TABS.map((tab) => tab.path).find((path) => segments.includes(path)) ?? 'web'

  // A scope that owns neither product is sold both at once, rather than being told about One on the Web
  // tab and about Node on the next three. The page stays drawn underneath — the tab bar and the shape of
  // the table it would hold — because that is what says how much of this page is behind the offer, and an
  // offer over nothing reads as a page that does not exist.
  const ownsNothing = !AD_UNIT_TABS.some((tab) => isTabAvailable(tab, products))

  /*
   * The same offer the Catalog shows, opened the same way: one card, over the page, with a close button.
   * The two surfaces used to differ on purpose — a modal where there was something behind worth keeping,
   * a blurred page where there was not — but a reader arriving on Ad units with neither product meets the
   * offer here as well, and one pitch that behaves the same everywhere beats two that argue about it.
   *
   * `bare` hands the modal's surface to the card, so it is the card rather than a card inside a white box.
   *
   * Closing it leaves the page rather than dismissing to it: what is underneath is the drawing of a table
   * this scope has no rows for, so the way out of the offer is the way back to wherever the reader came
   * from. A first entry into the app has no such page — a bookmark, a fresh tab — and falls back to the
   * same overview an unknown URL does.
   *
   * Opened once, by a ref rather than by the effect's dependencies: the modal handler is rebuilt whenever
   * the modal opens, so depending on it would reopen what the reader just closed.
   */
  const modal = useGlobalModal()
  const offerShown = useRef(false)
  useEffect(() => {
    if (!ownsNothing || offerShown.current) return
    offerShown.current = true
    modal.open(UpgradeModal, { offers: UPGRADE_OFFERS_TO_SELL }, { bare: true, onAfterDefaultClose: () => (location.key === 'default' ? navigate('/analytics/overview') : navigate(-1)) })
  }, [ownsNothing, modal, location.key, navigate])

  /* The crown marks the tabs whose product the scope has not bought — the same glyph the tab's own
     upgrade card wears, so the bar says how much of this page is for sale before anything is clicked.
     Tabs stay clickable when at least one product is owned: hiding them is what made the products
     invisible to the customers who might buy them, and a tab that cannot be opened cannot pitch. Once the
     scope owns none of them there is nothing left to switch to, so the bar is `disabled`: every tab leads
     to the same offer already on screen, and four clicks that do nothing say less than a bar plainly off. */
  const tabs: TabProp[] = AD_UNIT_TABS.map((tab) => ({
    label: tab.label,
    trackingLabel: tab.trackingLabel,
    active: tab.path === activePath,
    disabled: ownsNothing,
    ...(!isTabAvailable(tab, products)
      ? { iconName: 'crown' as const, iconFill: 'var(--color-king)' }
      : // Staff-only Node: usable by Pubstack admins alone, marked with the shield instead of the crown.
        isStaffOnNode && tab.offer === 'node'
        ? { iconName: 'admin' as const, iconFill: Colors.SlateGrey }
        : {}),
  }))

  /*
   * A tab the scope cannot use answers with the offer rather than with a page: the crown on the label
   * already says the tab is for sale, and this is the reader acting on it. Opening the pitch in place
   * keeps the work where it is — closing the offer leaves the tab they were on still open, with whatever
   * they had filtered or selected intact, which navigating to a locked page and back would have lost.
   *
   * The tab's own `offer`, not both: here the reader has pointed at one product, unlike a scope that owns
   * neither and is sold both at once above.
   */
  const onTabClick = (clicked: TabProp) => {
    const tab = AD_UNIT_TABS.find((candidate) => candidate.label === clicked.label)
    if (!tab) return
    if (!isTabAvailable(tab, products)) {
      modal.open(UpgradeModal, { offers: [tab.offer] }, { bare: true })
      return
    }
    navigate(tab.path)
  }

  /* Help belongs beside the page's title, not down in a row of table actions — it answers a question about
     the page, not about the table. It could not live here while it was one button for four tabs: it opened
     Node's flyout on the web tab too, the server-side copy answering a question about a web ad unit. So the
     shell reads the open tab and opens that tab's own flyout, which is what makes one button correct. */
  const openHelp = () =>
    activePath === 'web'
      ? flyout.open(PureAdmAdUnitHelpFlyout, { color: Colors.Turquoise, closeTrackingLabel: 'adm-ad-units-help-close' }, undefined)
      : flyout.open(AdUnitsHelpFlyout, { color: Colors.Turquoise, width: HELP_FLYOUT_WIDTH, closeTrackingLabel: 'pbs-ad-units-help-close' }, undefined)

  const tabBar = (
    <div className={'mb-5'}>
      <Tabs tabs={tabs} onClick={onTabClick} />
    </div>
  )

  const page = (children: ReactNode) => (
    <PageContent
      breadcrumbs={breadcrumbs}
      headerActions={
        <Button variant={'tertiary'} iconName={'help'} onClick={openHelp} trackingLabel={activePath === 'web' ? 'adm-ad-units-help' : 'pbs-ad-units-help'}>
          Help
        </Button>
      }
    >
      {children}
    </PageContent>
  )

  return page(
    <>
      {tabBar}
      {ownsNothing ? <LockedTableDrawing /> : <Outlet />}
    </>
  )
}
